import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle
} from '@/components/ui/drawer'
import { Spinner } from '@/components/ui/spinner'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { invalidStateMessage } from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { type MarqueeEventState, markUnresolvable } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Row targeted by the dialog (walk-in or invitee, from the export preview). */
export interface UnresolvableTarget {
	attendeeId: string
	name: string
	hostId?: string
}

interface MarkUnresolvableDialogProps {
	eventId?: string
	/**
	 * The event's lifecycle state, used only to word a wrong-state refusal: the
	 * write answers the shared 409 `invalid-state`, which names no action.
	 */
	eventState?: MarqueeEventState
	/** The row to mark; the dialog is open while this is set. */
	target: UnresolvableTarget | null
	onClose: () => void
	/**
	 * Fired once the row is actually marked, on top of `onClose`. Cancelling and
	 * confirming leave this dialog the same way but not the screen behind it: when
	 * it opens over the assign-host dialog, a cancel goes back to the search and a
	 * confirm is the end of the whole errand.
	 */
	onMarked?: () => void
}

/** Body shared between the desktop dialog and the mobile drawer. */
function DialogContentBody({ target }: { target: UnresolvableTarget }) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-col gap-4 text-neutral-500 text-sm'>
			<p className='whitespace-pre-line'>
				{t('marquee:export_unresolvable_body')}
			</p>
			<div className='rounded-2xl border-blue-500 border-l-4 bg-blue-50 px-4 py-3'>
				<p className='font-medium text-neutral-900'>
					{t('marquee:export_unresolvable_hint_title')}
				</p>
				<p>{t('marquee:export_unresolvable_hint_body')}</p>
			</div>
			<span className='sr-only'>{target.name}</span>
		</div>
	)
}

/**
 * "Mark row as unresolvable?" confirmation. Excludes the row (walk-in or
 * invitee) from the export (unblocking the gate) via
 * POST /attendees/{id}/mark-unresolvable, with an audit reason.
 * Desktop → `Dialog`, mobile → bottom `Drawer`.
 */
export function MarkUnresolvableDialog({
	eventId,
	eventState,
	target,
	onClose,
	onMarked
}: MarkUnresolvableDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const queryClient = useQueryClient()

	// Excluding the row unblocks the export gate, so the roster, the gate and
	// the export preview all change: one event prefix covers them.
	const mark = useMutation({
		mutationFn: (attendeeId: string) =>
			markUnresolvable({ path: { attendeeId }, body: {} }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			onClose()
			onMarked?.()
		},
		onError: (error) => {
			// One of seven writes sharing the 409 `invalid-state` code, whose own
			// sentence names no action. This one does.
			const refusal = invalidStateMessage({
				error,
				action: 'mark_unresolvable',
				eventState
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		}
	})

	const open = target !== null

	const confirm = () => {
		if (target) {
			mark.mutate(target.attendeeId)
		}
	}

	const title = t('marquee:export_unresolvable_title')
	// The row's identity leads the sentence in bold, per design, so the copy is
	// split: subject ("name (host_id …)") + the consequence line. A row with no
	// host id is exactly the row this dialog exists for, so the parenthetical is
	// dropped rather than filled with a dash: naming a field that is empty is
	// what the operator already knows and cannot act on here.
	const subtitle = target ? (
		<>
			<span className='font-semibold text-neutral-900'>
				{target.hostId
					? t('marquee:export_unresolvable_subject', {
							name: target.name,
							host: target.hostId
						})
					: target.name}
			</span>{' '}
			{t('marquee:export_unresolvable_subtitle')}
		</>
	) : (
		''
	)
	const confirmLabel = t('marquee:export_unresolvable_confirm')

	if (isDesktop) {
		return (
			<Dialog onOpenChange={(next) => !next && onClose()} open={open}>
				<DialogContent className='max-w-140 grid-rows-[minmax(0,1fr)_auto]'>
					<DialogBody className='gap-3'>
						<div className='flex flex-col gap-1 text-center'>
							<DialogTitle>{title}</DialogTitle>
							<DialogDescription className='text-neutral-500'>
								{subtitle}
							</DialogDescription>
						</div>
						{target && <DialogContentBody target={target} />}
					</DialogBody>
					<DialogFooter className='flex flex-row justify-end gap-2'>
						<DialogClose
							render={<Button type='button' variant='tertiary' />}
						>
							{t('marquee:cancel')}
						</DialogClose>
						<Button
							disabled={mark.isPending}
							onClick={confirm}
							type='button'
							variant='primary'
						>
							{confirmLabel}
							{mark.isPending && <Spinner size={16} />}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer onOpenChange={(next) => !next && onClose()} open={open}>
			<DrawerContent>
				<DrawerHeader className='pb-2'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{title}
					</DrawerTitle>
					<DrawerDescription className='text-neutral-500'>
						{subtitle}
					</DrawerDescription>
				</DrawerHeader>
				<div className='min-h-0 flex-1 overflow-y-auto px-4'>
					{target && <DialogContentBody target={target} />}
				</div>
				<DrawerFooter className='flex-row justify-end gap-2'>
					<DrawerClose asChild>
						<Button type='button' variant='tertiary'>
							{t('marquee:cancel')}
						</Button>
					</DrawerClose>
					<Button
						disabled={mark.isPending}
						onClick={confirm}
						type='button'
						variant='primary'
					>
						{confirmLabel}
						{mark.isPending && <Spinner size={16} />}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
